import { ApiService } from "../config";

/**
 * Notificações no celular (PWA). O service worker (public/sw.js) só recebe e
 * abre notificações; não guarda cache.
 *
 * iPhone: o Safari só entrega notificação para o app instalado na Tela de
 * Início (iOS 16.4+), e a permissão só pode ser pedida de dentro dele.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let convite: BeforeInstallPromptEvent | null = null;
const ouvintes = new Set<() => void>();

export const iniciarApp = () => {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (evento) => {
    evento.preventDefault();
    convite = evento as BeforeInstallPromptEvent;
    ouvintes.forEach((avisar) => avisar());
  });
  window.addEventListener("appinstalled", () => {
    convite = null;
    ouvintes.forEach((avisar) => avisar());
  });
  if ("serviceWorker" in navigator && window.isSecureContext) {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  }
};

export const aoMudarConvite = (avisar: () => void) => {
  ouvintes.add(avisar);
  return () => ouvintes.delete(avisar);
};

export const podeInstalar = () => convite !== null;

export const instalar = async (): Promise<boolean> => {
  if (!convite) return false;
  await convite.prompt();
  const { outcome } = await convite.userChoice;
  convite = null;
  ouvintes.forEach((avisar) => avisar());
  return outcome === "accepted";
};

export const ehIphone = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const estaInstalado = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const suportaNotificacao = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export type SituacaoDasNotificacoes =
  | "sem-suporte"
  | "iphone-instalar"
  | "bloqueadas"
  | "desativadas"
  | "ativadas";

export const situacaoAtual = async (): Promise<SituacaoDasNotificacoes> => {
  if (ehIphone() && !estaInstalado()) return "iphone-instalar";
  if (!suportaNotificacao()) return "sem-suporte";
  if (Notification.permission === "denied") return "bloqueadas";
  const registro = await navigator.serviceWorker.getRegistration();
  const inscricao = await registro?.pushManager.getSubscription();
  return inscricao && Notification.permission === "granted" ? "ativadas" : "desativadas";
};

const chaveParaBytes = (base64Url: string) => {
  const base64 = (base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const bruto = atob(base64);
  return Uint8Array.from(bruto, (letra) => letra.charCodeAt(0));
};

/** Pede permissão, inscreve o aparelho e registra no servidor. Chamar num clique. */
export const ativarNotificacoes = async (): Promise<SituacaoDasNotificacoes> => {
  const permissao = await Notification.requestPermission();
  if (permissao !== "granted") return permissao === "denied" ? "bloqueadas" : "desativadas";

  const resposta = await ApiService.get("/push/public-key");
  const chave: string | null = resposta?.data?.data?.chavePublica ?? null;
  if (!resposta?.data?.data?.ligado || !chave) {
    throw new Error("As notificações ainda não estão ligadas no servidor.");
  }

  const registro = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register("/sw.js"));
  await navigator.serviceWorker.ready;
  const existente = await registro.pushManager.getSubscription();
  const inscricao =
    existente ??
    (await registro.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: chaveParaBytes(chave) }));
  await ApiService.post("/push/subscriptions", inscricao.toJSON());
  return "ativadas";
};

export const desativarNotificacoes = async () => {
  const registro = await navigator.serviceWorker.getRegistration();
  const inscricao = await registro?.pushManager.getSubscription();
  if (!inscricao) return;
  await ApiService.delete("/push/subscriptions", { endpoint: inscricao.endpoint }).catch(() => undefined);
  await inscricao.unsubscribe();
};

export const enviarTeste = async (): Promise<number> => {
  const resposta = await ApiService.post("/push/subscriptions/test", {});
  return Number(resposta?.data?.data ?? 0);
};

/** O app abriu a partir de uma notificação (?n=ref): conta como "relatório aberto". */
export const registrarAbertura = (ref: string) =>
  ApiService.post(`/push/events/${ref}/abertura`, {}).catch(() => undefined);

/* Equipe: situação e envio para os clientes de um perfil. */

export type HistoricoDeEnvio = {
  enviadaEm: string;
  tipo: "MANUAL" | "RESUMO_DIARIO" | "RESUMO_SEMANAL";
  categoria?: string | null;
  link?: string | null;
  titulo: string;
  texto: string;
  aparelhos: number;
  /** Aceitos pelo provedor de push (não prova que apareceu no celular). */
  entregues: number;
  recebidos?: number;
  cliques?: number;
  aberturas?: number;
};

export type SituacaoDoPerfil = {
  ligado: boolean;
  perfil: string;
  clientes: number;
  clientesComAparelho: number;
  aparelhos: number;
  ultimas24h?: number;
  ultimos7dias?: number;
  tetoDia?: number;
  tetoSemana?: number;
  historico: HistoricoDeEnvio[];
};

export const situacaoDoPerfil = async (publicId: string): Promise<SituacaoDoPerfil> => {
  const resposta = await ApiService.get(`/push/profile/${publicId}`);
  return resposta.data.data as SituacaoDoPerfil;
};

export type MensagemDePush = { title: string; body: string; url?: string; category?: string; force?: boolean };

export const enviarAoPerfil = async (
  publicId: string,
  mensagem: MensagemDePush,
): Promise<{ clientes: number; aparelhos: number; entregues: number }> => {
  const resposta = await ApiService.post(`/push/profile/${publicId}/send`, mensagem);
  return resposta.data.data;
};

/** Equipe: manda a mensagem só para os próprios aparelhos, para conferir antes de enviar ao cliente. */
export const enviarTesteDaMensagem = async (mensagem: MensagemDePush): Promise<number> => {
  const resposta = await ApiService.post("/push/subscriptions/test", mensagem);
  return Number(resposta?.data?.data ?? 0);
};
